/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, {
    useEffect, useRef, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import {
    any, object, func, string, bool, number,
} from 'prop-types';
import mbpUtil from '@api-util/mbp';
import mbpLogger from '@module-logger/mbp';
import { useSelector, useDispatch } from 'react-redux';
import { getAccessToken } from '../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getAccessTokenSafely } from '../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { trackEvent } from '../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import ConversationalQualtricsFeedBackContainer from './ConversationalUIFeedbackContainer';
import getTrustedOrigin from './helper/TrustedOrigin';
import { isLauncherVisible } from './chatExperiences';
import { validateProductFilterZipcode } from '../../../state/ducks/App/App-Actions';

const FLORA_MESSAGE_TYPES = new Set([
    'flora-conv-ui-session-info',
    'flora-get-access-token',
    'flora-get-page-info',
    'flora-navigateProductPage',
    'flora-chat-iframe-maximised',
    'flora-chat-iframe-maximised-sales-intent',
    'flora-chat-iframe-minimised',
    'flora-chat-iframe-display',
    'flora-chat-preview-msg-closed',
    'flora-preview-msg-ready',
    'flora-no-preview-msg',
    'flora-end-chat-hide-button',
    'flora-chat-trigger-event-tracking',
    'flora-chat-set-zipcode',
]);

const ConversationalUIChatBot = ({
    deviceType, brand, browser, operatingSystem, brandTheme, pageType, location, setIsFloraChatBtnClicked, featureFlags,
    experienceId, chatRoute, active, launcherVisible, launcherOffset, trigger, onActivate, onMinimize, onEndChat, onScrollLock,
}) => {
    const history = useHistory();
    const iframeRef = useRef(null);
    const pageTypeRef = useRef(pageType);
    const [isIframeMaximized, setIsIframeMaximized] = useState(false);
    const isMobile = deviceType !== 'desktop';
    const [isIframeLoaded, setIsIframeLoaded] = useState(true);
    const trustedOrigin = getTrustedOrigin(mbpUtil?.getEnv('APP_CONVERSATIONAL_UI_HOST'));
    const cartItems = useSelector((state) => state?.cart?.recipients);
    const cartHasPassport = useRef('false');
    const orderDetails = useSelector((state) => state?.cart);
    const ORDER_PLACED = 'Published';
    const dispatch = useDispatch();
    const [orderSize, setOrderSize] = useState(0);
    const accessToken = useSelector(getAccessToken);
    const isFloraChatQualtricsSurveyButtonEnabled = featureFlags['is-flora-chat-persistent-qualtrics-survey-button-enabled'];
    const [feedbackMinimized, setFeedbackMinimized] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [isFeedbackButtonDisplayed, setIsFeedbackButtonDisplayed] = useState(isFloraChatQualtricsSurveyButtonEnabled);
    const [buttonDismissed, setButtonDismissed] = useState(false);
    // setReady(true) after sharing session data with the iframe/conv-ui
    const [ready, setReady] = useState(false);
    const lastTrigger = useRef(null);
    const getSessionData = (token) => {
        const url = window.location.href;
        const browseruuid = localStorage?.getItem('browserUUID') || '';

        if (brand?.code === 'CSG' || brand?.version === '5.0') {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

            return {
                url,
                brand,
                browser,
                deviceType,
                operatingSystem,
                brandTheme,
                auth: { isAuthenticated: !!userInfo?.profile?.email },
                profile: userInfo?.profile,
                enterprise: { enterpriseId: '' },
                browseruuid,
                token: userInfo?.token || token || '',
            };
        }

        const memberSession = JSON.parse(sessionStorage.getItem('persist:member') || '{}');
        const auth = JSON.parse(memberSession?.auth || '{}');
        const profile = JSON.parse(memberSession?.profile || '{}');
        const enterprise = JSON.parse(memberSession?.enterpriseId || '{}');

        return {
            url,
            brand,
            browser,
            deviceType,
            operatingSystem,
            brandTheme,
            auth,
            profile,
            enterprise,
            browseruuid,
            token,
        };
    };

    const getFloraAccessToken = (token) => {
        if (brand?.code === 'CSG' || brand?.version === '5.0') {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            return userInfo?.token || token || '';
        }

        return token;
    };

    const resizeIframeForMaximizedChat = () => {
        setIsIframeMaximized(true);
        if (!iframeRef.current) return;

        if (isMobile) {
            iframeRef.current.style.top = '0';
            iframeRef.current.style.height = '100dvh';
            iframeRef.current.style.width = '100vw';
            iframeRef.current.style.right = '';
            iframeRef.current.style.bottom = '';
            return;
        }

        iframeRef.current.style.top = '';
        const screenWidth = window.innerWidth;
        if (screenWidth < 1024) {
            iframeRef.current.style.width = '45%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-7%';
            iframeRef.current.style.right = '0%';
        } else if (screenWidth >= 1024 && screenWidth <= 1280) {
            iframeRef.current.style.width = '40%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-6%';
            iframeRef.current.style.right = '0%';
        } else if (screenWidth > 1280 && screenWidth <= 1440) {
            iframeRef.current.style.width = '32%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-4%';
            iframeRef.current.style.right = '0%';
        } else if (screenWidth > 1440 && screenWidth <= 1920) {
            iframeRef.current.style.width = '30%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-4%';
            iframeRef.current.style.right = '2%';
        } else if (screenWidth > 1920) {
            iframeRef.current.style.width = '20%';
            iframeRef.current.style.height = '75%';
            iframeRef.current.style.bottom = '-5%';
            iframeRef.current.style.right = '1%';
        }

        if (isFloraChatQualtricsSurveyButtonEnabled) {
            setIsFeedbackButtonDisplayed(false);
            if (feedbackOpen === true) setFeedbackMinimized(!feedbackMinimized);
            setFeedbackOpen(false);
        }
    };

    const resizeIframeForMaximizedSalesIntentChat = () => {
        setIsIframeMaximized(true);
        if (!iframeRef.current) return;

        if (isMobile) {
            iframeRef.current.style.top = '0';
            iframeRef.current.style.height = '100dvh';
            iframeRef.current.style.width = '100vw';
            iframeRef.current.style.right = '';
            iframeRef.current.style.bottom = '';
            return;
        }

        iframeRef.current.style.top = '';
        iframeRef.current.style.width = '100%';
        iframeRef.current.style.height = '100%';
        iframeRef.current.style.bottom = '0';
        iframeRef.current.style.right = '0%';

        if (isFloraChatQualtricsSurveyButtonEnabled) {
            setIsFeedbackButtonDisplayed(false);
            if (feedbackOpen === true) setFeedbackMinimized(!feedbackMinimized);
            setFeedbackOpen(false);
        }
    };

    const resizeIframeForMinimizedChat = () => {
        setIsIframeMaximized(false);
        if (!iframeRef.current) return;

        if (experienceId === 'gemini') {
            iframeRef.current.style.width = '100vw';
            iframeRef.current.style.height = '100vh';
            iframeRef.current.style.top = '0';
            iframeRef.current.style.bottom = '0';
            iframeRef.current.style.right = '-100%';
        } else {
            iframeRef.current.style.width = '120px';
            iframeRef.current.style.height = '80px';
            iframeRef.current.style.top = '';
            iframeRef.current.style.bottom = `${launcherOffset}px`;
            iframeRef.current.style.right = '0%';
        }
        if (isFloraChatQualtricsSurveyButtonEnabled && !isMobile) {
            setIsFeedbackButtonDisplayed(true);
        }
    };

    const resizeIframeForPreview = (showPreview) => {
        // Late preview notifications must not resize an expanded conversation.
        if (isIframeMaximized || !iframeRef.current) return;
        resizeIframeForMinimizedChat();
        if (showPreview) {
            iframeRef.current.style.width = '350px';
            iframeRef.current.style.height = '150px';
        }
    };

    useEffect(() => {
        // Initialize geometry and reposition minimized launchers when their slot changes.
        // Activation only controls visibility; it must not overwrite a resized frame.
        if (!isIframeMaximized) resizeIframeForMinimizedChat();
    }, [launcherOffset]);

    useEffect(() => {
        onScrollLock(experienceId, active && isIframeMaximized);
        return () => onScrollLock(experienceId, false);
    }, [active, isIframeMaximized, experienceId, onScrollLock]);

    // Session-info is the existing app-level handshake: the iframe listener is installed.
    useEffect(() => {
        if (!ready || !active || !trigger || lastTrigger.current === trigger) return;
        lastTrigger.current = trigger;
        setButtonDismissed(false);
        if (isIframeMaximized && ['trigger-flora-chat', 'chat-header-button'].includes(trigger.message.type)) return;
        iframeRef.current?.contentWindow?.postMessage(trigger.message, trustedOrigin);
    }, [ready, active, trigger, trustedOrigin]);

    useEffect(() => {
        if (accessToken) {
            try {
                const auth = JSON.parse(JSON.parse(sessionStorage.getItem('persist:member'))?.auth);
                const iframe = iframeRef.current;
                if (iframe && iframe?.contentWindow) {
                    iframe.contentWindow.postMessage(
                        { type: 'flora-update-access-token', data: auth },
                        trustedOrigin,
                    );
                }
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    errorMessage: error,
                    message: `FloraChat: Token Issue - ERROR: ${error}`,
                });
            }
        }
    }, [accessToken, ready]);

    useEffect(() => {
        cartHasPassport.current = 'false';
        cartItems?.forEach((cartItem) => {
            cartItem?.cartItems?.forEach((item) => {
                if (item?.itemInfo?.lineItemType === 'PASSPORT') {
                    cartHasPassport.current = 'true';
                }
            });
        });
        const iframe = iframeRef.current;
        if (iframe && iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                { type: 'flora-update-cart-passport-status', data: { cartHasPassport: cartHasPassport.current } },
                trustedOrigin,
            );
        }
    }, [cartItems, ready]);

    useEffect(() => {
        pageTypeRef.current = pageType;
        const iframe = iframeRef.current;
        if (iframe && iframe?.contentWindow) {
            iframe.contentWindow.postMessage(
                { type: 'flora-update-page-info', data: pageTypeRef.current }, trustedOrigin,
            );
        }
    }, [pageType, ready]);

    useEffect(() => {
        if (ready) {
            const customParameters = {
                currentUrl: window?.location?.href,
            };
            const iframe = iframeRef.current;
            if (iframe && iframe.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'flora-update-visiting-page', data: customParameters },
                    trustedOrigin,
                );
            }
        }
    }, [location?.pathname, ready, trustedOrigin]);

    /**
     * send order details to the connected agent whenever the user add or remove something.
     */
    useEffect(() => {
        if (ready && orderDetails && orderDetails.orderSize !== orderSize) {
            const itemAdded = orderDetails.orderSize > orderSize;
            setOrderSize(orderDetails.orderSize);
            const customParameters = {
                isItemAdded: itemAdded,
                orderValue: orderDetails.cartTotal,
                orderNumber: orderDetails.orderIdWithSourceId,
            };
            const iframe = iframeRef.current;
            if (iframe && iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'flora-update-cart-details', data: customParameters },
                    trustedOrigin,
                );
            }
        }
    }, [orderDetails, ready]);

    useEffect(() => {
        if (orderDetails?.cartStatus && orderDetails?.cartStatus === ORDER_PLACED) {
            const customParameters = {
                wasOrderPlaced: 'true',
                orderNumber: orderDetails?.cartId,
            };
            const iframe = iframeRef.current;
            if (iframe && iframe?.contentWindow) {
                iframe.contentWindow.postMessage(
                    { type: 'flora-update-order-placed-status', data: customParameters },
                    trustedOrigin,
                );
            }
        }
    }, [orderDetails?.cartStatus, ready]);

    useEffect(() => {
        const handleMessage = async (event) => {
            try {
                const iframe = iframeRef.current;
                const type = event?.data?.type;

                if (
                    !iframe?.contentWindow
                    || event.origin !== trustedOrigin
                    || typeof type !== 'string'
                    || !FLORA_MESSAGE_TYPES.has(type)
                    || event.source !== iframe.contentWindow
                ) {
                    return;
                }

                let token = accessToken;
                const env = (mbpUtil.getEnv('APP_GRAPHQL_ENV') || '')?.toLowerCase();
                if (env && !env.includes('prod') && ['flora-conv-ui-session-info', 'flora-get-access-token'].includes(type)) {
                    token = await getAccessTokenSafely();
                }

                if (type === 'flora-conv-ui-session-info') {
                    event.source.postMessage({
                        type: 'flora-session-storage-data',
                        data: getSessionData(token),
                    }, trustedOrigin);
                    setReady(true);
                }
                if (type === 'flora-get-access-token') {
                    iframe.contentWindow.postMessage(
                        { type: 'flora-update-access-token', data: getFloraAccessToken(token) },
                        trustedOrigin,
                    );
                }
                if (type === 'flora-get-page-info') {
                    setIsFloraChatBtnClicked(true);
                    iframe.contentWindow.postMessage(
                        { type: 'flora-update-page-info', data: pageTypeRef.current },
                        trustedOrigin,
                    );
                }
                if (type === 'flora-navigateProductPage') {
                    // window.location.href = event.data.url;
                    const target = new URL(event.data.url, window.location.origin);
                    if (target.origin === window.location.origin) {
                        // client-side nav keeps the chat iframes mounted.
                        history.push(`${target.pathname}${target.search}${target.hash}`);
                    } else if (['http:', 'https:'].includes(target.protocol)) {
                        window.location.href = target.href;
                    }
                }
                if (type === 'flora-chat-iframe-maximised') {
                    setButtonDismissed(false);
                    resizeIframeForMaximizedChat();
                    onActivate(experienceId);
                }
                if (type === 'flora-chat-iframe-maximised-sales-intent') {
                    setButtonDismissed(false);
                    resizeIframeForMaximizedSalesIntentChat();
                    onActivate(experienceId);
                }
                if (type === 'flora-chat-iframe-minimised') {
                    resizeIframeForMinimizedChat();

                    onMinimize(experienceId);
                }
                if (type === 'flora-chat-iframe-display' && active) {
                    setButtonDismissed(false);
                    onActivate(experienceId);
                }
                if (type === 'flora-chat-preview-msg-closed' || type === 'flora-no-preview-msg') {
                    resizeIframeForPreview(false);
                }
                if (type === 'flora-preview-msg-ready') {
                    resizeIframeForPreview(true);
                }
                if (type === 'flora-end-chat-hide-button') {
                    if (!isLauncherVisible(experienceId, featureFlags, location?.pathname, pageType)) setButtonDismissed(true);

                    resizeIframeForMinimizedChat();
                    onEndChat(experienceId);
                }
                if (type === 'flora-chat-trigger-event-tracking') {
                    const trackingData = event?.data?.tracking_data;
                    if (trackingData) {
                        dispatch(trackEvent(trackingData));
                    }
                }
                if (type === 'flora-chat-set-zipcode') {
                    const isCanadaZip = (zipcode) => new RegExp(/^[a-zA-Z]{1}[0-9]{1}[a-zA-Z]{1}[- ]{0,1}[0-9]{1}[a-zA-Z]{1}[0-9]{1}/).test(zipcode); // Canada zip code
                    let userSubmittedZipCode = event?.data?.zipCode;
                    if ((userSubmittedZipCode.length === 6 || userSubmittedZipCode.length === 7) && isCanadaZip(userSubmittedZipCode)) {
                        // if user enters 6 or 7 chars and is canada zipcode
                        const zipSplit = userSubmittedZipCode.replace(/\s/gi, '').toUpperCase().split('');
                        zipSplit.splice(3, 0, ' ');
                        const canadaZipCode = zipSplit.join('');
                        userSubmittedZipCode = canadaZipCode;
                        dispatch(validateProductFilterZipcode(canadaZipCode));
                    } else if (userSubmittedZipCode.length === 5 && /^\d+$/.test(userSubmittedZipCode) === true) {
                        dispatch(validateProductFilterZipcode(userSubmittedZipCode));
                    }
                }
            } catch (error) {
                mbpLogger.logError({
                    appName: process.env.npm_package_name,
                    errorMessage: error,
                    message: `FloraChat: Could not post value - ERROR: ${error}`,
                });
            }
        };
        if (isIframeLoaded) {
            window?.addEventListener('message', handleMessage);
        }
        return () => {
            window?.removeEventListener('message', handleMessage);
        };
    });

    const handleIframeLoad = () => {
        setIsIframeLoaded(true);

        dispatch(trackEvent({
            eventCategory: '18FChatBot',
            eventAction: 'Chat Loaded',
            eventLabel: '18FChatBot',
            interaction_type: 'click',
            ga_event_name: 'chat_interaction',
        }));
    };
    const handleIframeError = () => setIsIframeLoaded(false);

    return isIframeLoaded ? (
        <>
            {active && experienceId === 'customerService' && <ConversationalQualtricsFeedBackContainer featureFlags={featureFlags} isMobile={isMobile} brand={brand} feedbackMinimized={feedbackMinimized} setFeedbackMinimized={setFeedbackMinimized} feedbackOpen={feedbackOpen} setFeedbackOpen={setFeedbackOpen} isFeedbackButtonDisplayed={isFeedbackButtonDisplayed} />}
            {launcherVisible && !buttonDismissed && isIframeMaximized && !active && (
                <button
                    type="button"
                    onClick={() => onActivate(experienceId)}
                    style={{ position: 'fixed', bottom: launcherOffset, right: 0, zIndex: 2243000 }}
                >
                    Resume {{ customerService: 'customer service', salesIntent: 'Sales Intent', gemini: 'Gemini' }[experienceId]} chat
                </button>
            )}
            <iframe
                id={experienceId === 'customerService' ? 'chatbot' : `chatbot-${experienceId}`}
                ref={iframeRef}
                title={`Chat: ${experienceId}`}
                allowFullScreen
                style={{
                    overflow: 'hidden',
                    boxShadow: 'none',
                    border: 'none',
                    position: 'fixed',
                    zIndex: 2243000,
                    display: active || (launcherVisible && !buttonDismissed && !isIframeMaximized) ? '' : 'none',
                }}
                src={`${trustedOrigin}${chatRoute}`}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
            />
        </>
    ) : null;
};

ConversationalUIChatBot.propTypes = {
    deviceType: string.isRequired,
    brand: object.isRequired,
    browser: string.isRequired,
    operatingSystem: string.isRequired,
    brandTheme: any.isRequired,
    pageType: string.isRequired,
    location: object.isRequired,
    setIsFloraChatBtnClicked: func.isRequired,
    featureFlags: object.isRequired,
    experienceId: string.isRequired,
    chatRoute: string.isRequired,
    active: bool.isRequired,
    launcherVisible: bool.isRequired,
    launcherOffset: number.isRequired,
    trigger: object,
    onActivate: func.isRequired,
    onMinimize: func.isRequired,
    onEndChat: func.isRequired,
    onScrollLock: func.isRequired,
};
ConversationalUIChatBot.defaultProps = { trigger: null };
export default ConversationalUIChatBot;
